// SPDX-License-Identifier: Apache-2.0

// /temple — the embodied chat surface (the actual temple, not a placeholder).
//
// CHOICE: iframe-mount the existing harness at
// `~/Downloads/html-in-canvas/eval/harness-temple.html`, served by the static
// server that `summon_temple` (MCP tool) starts on port 8000.
//
// Why iframe over Solid+three.js port:
//   1. The harness is ~700 lines of three.js + WS plumbing, already shipping.
//   2. Iframe sandboxing isolates its three.js renderer + WebGL context from
//      the SPA's own kernel benches (which also use WebGPU/WebGL).
//   3. Bug-for-bug parity with the standalone product. No bifurcation.
//   4. We can revisit a Solid port later without changing this route's contract.
//
// The downside (iframe sandbox isolation, a tiny URL-bar feel inside the
// frame) is acceptable for v1 — the harness is already framed in the brand's
// dark backdrop, so the seam is invisible at full-bleed.
//
// Lifecycle is owned by <TempleSummonGate>: it calls the MCP `summon_temple`
// tool over ws://localhost:5417/ws on mount, shows a tasteful pulsing-star
// "starting" state, and calls `dismiss_temple` on unmount (only if THIS
// route was the summoner — the gate enforces idempotency).
//
// Backend-aware shell — when the SPA is served from a deployed origin that
// has no local MCP server (Pages preview, public Pages production, etc.),
// the gate would just hang on a 4s WS timeout and then surface a cryptic
// "MCP server unreachable" diagnostic. We pre-empt that with an explicit
// banner that explains what /temple is and how to reach it.

import { Show } from "solid-js";
import TempleSummonGate from "../components/temple/TempleSummonGate";
import { isMcpReachable } from "../lib/api";
import "../components/temple/temple.css";

const JOURNEY_URL = "http://localhost:8001/journey/journey.html";

export default function Temple() {
  const reachable = isMcpReachable();

  return (
    <div class="temple-route" data-testid="temple-route">
      <div class="temple-route__eyebrow" aria-hidden="true">
        embodied chat
      </div>

      <Show
        when={reachable}
        fallback={
          <div
            class="empty-state"
            data-testid="temple-no-backend"
            style={{ "max-width": "720px", margin: "48px auto" }}
          >
            <div class="kicker" style={{ "margin-bottom": "12px" }}>
              /temple — embodied chat
            </div>
            <h2 style={{ "margin-bottom": "12px" }}>
              the temple needs the local backend
            </h2>
            <p>
              <strong>the temple</strong> is a multi-Claude orchestration
              surface — a 3D face you can talk to, driven by a local{" "}
              <code>claude</code> binary. It runs entirely on your machine: a
              relay on port 8788, a static asset server on port 8000, and an
              MCP server on port 5417.
            </p>
            <p>
              MCP is not reachable on this origin (<code>{
                typeof window !== "undefined" ? window.location.host : "?"
              }</code>). To open the temple, run tether-brain locally:
            </p>
            <pre
              style={{
                "text-align": "left",
                margin: "16px auto",
                padding: "12px 16px",
                "max-width": "520px",
                "background": "rgba(0,0,0,0.25)",
                "border-radius": "6px",
                "font-size": "13px",
                "white-space": "pre-wrap",
              }}
            >{`cd ~/tether-brain-v2/scaffold
bun run dev:mcp     # MCP server on :5417
bun run dev         # SPA on :5173
# then open http://localhost:5173/temple`}</pre>
            <p class="muted" style={{ "font-size": "12px" }}>
              public hosts can't summon temples — the embodied loop spawns
              real subprocesses, so it must run where you are.
            </p>
          </div>
        }
      >
        <TempleSummonGate>
          {(iframeUrl) => (
            <iframe
              class="temple-route__frame"
              src={iframeUrl}
              title="the temple — embodied chat"
              allow="microphone; clipboard-read; clipboard-write"
              data-testid="temple-iframe"
            />
          )}
        </TempleSummonGate>
      </Show>

      {/* Picture-book journey footer — kept reachable for newcomers. */}
      <a
        class="temple-route__journey-link"
        href={JOURNEY_URL}
        target="_blank"
        rel="noreferrer"
      >
        for newcomers, see the picture-book journey
      </a>
    </div>
  );
}
